import type { DragEvent } from 'react'

/** What the panel is doing. */
export type ImportUploadState = 'idle' | 'uploading' | 'done' | 'error'

/**
 * Props for {@link ImportUploadPanel}.
 *
 * @property fileLabel - The logical file's display name (e.g. "Clients").
 * @property filenamePattern - The `web` method's pattern, shown as the accepted names.
 * @property state - What the panel is doing; `uploading` disables it.
 * @property statusText - The import's status once done (e.g. `SCANNING`).
 * @property message - The refusal or failure to show when `state` is `error`.
 * @property onFile - Called with the dropped or chosen file.
 */
export interface ImportUploadPanelProps {
	fileLabel: string
	filenamePattern: string
	state: ImportUploadState
	statusText?: string | null
	message?: string | null
	onFile: (file: File) => void
}

/**
 * The console's drop zone for one logical file's `web` upload (TO-392 REQ-014):
 * drop a file or choose one; the status, or the refusal, is announced. Pure:
 * the upload itself is the caller's.
 */
export function ImportUploadPanel({
	fileLabel,
	filenamePattern,
	state,
	statusText = null,
	message = null,
	onFile,
}: ImportUploadPanelProps) {
	const busy = state === 'uploading'
	const take = (file: File | undefined) => {
		if (file && !busy) onFile(file)
	}
	const onDrop = (event: DragEvent<HTMLFieldSetElement>) => {
		event.preventDefault()
		take(event.dataTransfer.files[0])
	}
	return (
		<fieldset
			aria-label={`Upload a ${fileLabel} file`}
			aria-busy={busy}
			onDragOver={(event) => event.preventDefault()}
			onDrop={onDrop}
			className="flex flex-col gap-2 rounded-ds-control border border-dashed border-ds-border bg-ds-surface-muted p-ds-4 text-ds-sm text-ds-text"
		>
			<p>
				Drop a file here to upload it. Accepted names: <code>{filenamePattern}</code>
			</p>
			<label className="flex flex-col gap-1">
				<span>Choose a file</span>
				<input
					type="file"
					disabled={busy}
					onChange={(event) => {
						take(event.target.files?.[0])
						event.target.value = ''
					}}
				/>
			</label>
			{state === 'error' && message ? (
				<p role="alert" className="text-ds-danger">
					{message}
				</p>
			) : (
				<p role="status" aria-live="polite">
					{busy ? 'Uploading…' : state === 'done' && statusText ? `Status: ${statusText}` : ''}
				</p>
			)}
		</fieldset>
	)
}
